
import React, { useState, useEffect } from 'react';

const API_URL = '';

export default function PreorderPanel() {
  const [preorders, setPreorders] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    revenue: 0,
    avgOrder: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPreorders();
  }, []);

  const fetchPreorders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/preorders`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      const data = await response.json();
      console.log('Full API response:', data); // Debug
      
      // Handle different response structures
      let items = [];
      if (data.items && Array.isArray(data.items)) {
        items = data.items;
      } else if (data.preorders && Array.isArray(data.preorders)) {
        items = data.preorders;
      } else if (Array.isArray(data)) {
        items = data;
      } else if (data.data && Array.isArray(data.data)) {
        items = data.data;
      }
      
      console.log('Processed items:', items.length); // Debug
      
      setPreorders(items);
      
      const total = items.length;
      // Calculate revenue from items (if they have price, otherwise 0)
      const revenue = items.reduce((sum, item) => sum + (item.price || 0), 0);
      const avgOrder = total > 0 ? revenue / total : 0;
      
      setStats({ total, revenue, avgOrder });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching preorders:', error);
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (preorders.length === 0) {
      alert('No preorders to export');
      return;
    }
    
    const csvContent = [
      ['ID', 'Product', 'User', 'Email', 'Date', 'Message'].join(','),
      ...preorders.map(p => [
        p.id || '',
        `"${(p.title || p.productName || 'N/A').replace(/"/g, '""')}"`,
        `"${(p.user || p.name || 'Anonymous').replace(/"/g, '""')}"`,
        p.email || 'N/A',
        new Date(p.date || p.created_at).toLocaleString(),
        `"${(p.message || '').replace(/"/g, '""')}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `preorders-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDelete = async (preorderId) => {
    if (!preorderId) {
      console.error('No preorder ID provided');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this preorder?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/preorders/${preorderId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('✅ Preorder deleted successfully!');
        fetchPreorders(); // Refresh the list
      } else {
        const error = await response.json();
        alert(`❌ Failed to delete preorder: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting preorder:', error);
      alert('❌ Error deleting preorder');
    }
  };

  const handleComplete = async (preorderId) => {
    if (!preorderId) {
      console.error('No preorder ID provided');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/preorders/${preorderId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'completed' })
      });

      if (response.ok) {
        alert('✅ Preorder marked as completed!');
        fetchPreorders();
      } else {
        alert('❌ Failed to update preorder');
      }
    } catch (error) {
      console.error('Error updating preorder:', error);
      alert('❌ Error updating preorder');
    }
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <p style={{ color: '#fff', marginTop: '20px' }}>Loading preorders...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Preorder Management</h2>
          <p style={styles.subtitle}>Track and manage all customer preorders</p>
        </div>
        <button style={styles.exportButton} onClick={handleExport}>
          📥 Export Preorders
        </button>
      </div>

      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statIcon}>🎯</div>
          <div style={styles.statContent}>
            <p style={styles.statLabel}>Total Preorders</p>
            <h3 style={styles.statValue}>{stats.total}</h3>
          </div>
        </div>
        
        <div style={styles.statCard}>
          <div style={styles.statIcon}>💰</div>
          <div style={styles.statContent}>
            <p style={styles.statLabel}>Total Revenue</p>
            <h3 style={styles.statValue}>${(stats.revenue / 100).toFixed(2)}</h3>
          </div>
        </div>
        
        <div style={styles.statCard}>
          <div style={styles.statIcon}>📊</div>
          <div style={styles.statContent}>
            <p style={styles.statLabel}>Avg Order Value</p>
            <h3 style={styles.statValue}>${(stats.avgOrder / 100).toFixed(2)}</h3>
          </div>
        </div>
      </div>

      <div style={styles.ordersSection}>
        <h3 style={styles.sectionTitle}>Recent Preorders</h3>
        
        {preorders.length === 0 ? (
          <div style={styles.empty}>
            <span style={styles.emptyIcon}>📋</span>
            <p style={styles.emptyText}>No preorders yet</p>
            <p style={styles.emptySubtext}>Preorders will appear here when customers place them</p>
          </div>
        ) : (
          <div style={styles.ordersList}>
            {preorders.slice(0, 20).map((order, index) => (
              <div key={order.id || index} style={styles.orderCard}>
                <div style={styles.orderHeader}>
                  <div style={styles.orderNumber}>
                    #{order.id || (preorders.length - index)}
                  </div>
                  <div style={styles.orderDate}>
                    {new Date(order.date || order.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
                
                <div style={styles.orderContent}>
                  <div style={styles.orderInfo}>
                    <div style={styles.orderProduct}>
                      <span style={styles.productIcon}>📦</span>
                      <span style={styles.productName}>{order.title || order.productName || 'Unknown Product'}</span>
                    </div>
                    <div style={styles.orderCustomer}>
                      <span style={styles.customerIcon}>👤</span>
                      <div>
                        <div style={styles.customerName}>{order.user || order.name || 'Anonymous'}</div>
                        <div style={styles.customerEmail}>{order.email || 'No email'}</div>
                      </div>
                    </div>
                  </div>
                  
                  {order.message && (
                    <div style={styles.orderMessage}>
                      <span style={styles.messageIcon}>💬</span>
                      <span style={styles.messageText}>{order.message}</span>
                    </div>
                  )}
                </div>
                
                <div style={styles.orderFooter}>
                  {order.price && (
                    <span style={styles.orderPrice}>
                      ${(order.price / 100).toFixed(2)}
                    </span>
                  )}
                  <div style={styles.orderActions}>
                    <button 
                      style={styles.actionButton}
                      onClick={() => handleComplete(order.id)}
                      title="Mark as completed"
                    >
                      ✅ Complete
                    </button>
                    <button 
                      style={styles.actionButtonDanger}
                      onClick={() => handleDelete(order.id)}
                      title="Delete preorder"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Styles object (same as yours, I'm keeping it compact here)
const styles = {
  container: { padding: '30px', backgroundColor: '#0a0a0a', minHeight: '100vh', color: '#fff' },
  loading: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', backgroundColor: '#0a0a0a' },
  spinner: { width: '50px', height: '50px', border: '4px solid rgba(206, 0, 33, 0.1)', borderTop: '4px solid #ce0021', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '30px' },
  title: { fontSize: '32px', fontWeight: 'bold', color: '#ce0021', margin: '0 0 8px 0' },
  subtitle: { fontSize: '16px', color: '#999', margin: 0 },
  exportButton: { padding: '12px 24px', background: 'linear-gradient(135deg, #ce0021 0%, #a00019 100%)', border: 'none', borderRadius: '8px', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '40px' },
  statCard: { background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)', border: '1px solid rgba(206, 0, 33, 0.2)', borderRadius: '16px', padding: '24px', display: 'flex', alignItems: 'center', gap: '16px' },
  statIcon: { fontSize: '36px', width: '60px', height: '60px', background: 'rgba(206, 0, 33, 0.1)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  statContent: { flex: 1 },
  statLabel: { fontSize: '14px', color: '#999', margin: '0 0 8px 0' },
  statValue: { fontSize: '28px', fontWeight: 'bold', color: '#ce0021', margin: 0 },
  ordersSection: { marginTop: '40px' },
  sectionTitle: { fontSize: '20px', fontWeight: 'bold', color: '#fff', marginBottom: '20px', borderLeft: '4px solid #ce0021', paddingLeft: '12px' },
  empty: { textAlign: 'center', padding: '60px 20px', background: 'rgba(255, 255, 255, 0.02)', borderRadius: '16px', border: '1px dashed rgba(255, 255, 255, 0.1)' },
  emptyIcon: { fontSize: '64px', display: 'block', marginBottom: '16px' },
  emptyText: { fontSize: '20px', color: '#fff', margin: '0 0 8px 0' },
  emptySubtext: { fontSize: '14px', color: '#999', margin: 0 },
  ordersList: { display: 'flex', flexDirection: 'column', gap: '16px' },
  orderCard: { background: 'linear-gradient(135deg, #1a1a1a 0%, #0f0f0f 100%)', border: '1px solid rgba(206, 0, 33, 0.2)', borderRadius: '12px', padding: '20px' },
  orderHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' },
  orderNumber: { fontSize: '18px', fontWeight: 'bold', color: '#ce0021' },
  orderDate: { fontSize: '13px', color: '#999' },
  orderContent: { marginBottom: '16px' },
  orderInfo: { display: 'flex', flexDirection: 'column', gap: '12px' },
  orderProduct: { display: 'flex', alignItems: 'center', gap: '12px' },
  productIcon: { fontSize: '20px' },
  productName: { fontSize: '16px', fontWeight: '600', color: '#fff' },
  orderCustomer: { display: 'flex', alignItems: 'center', gap: '12px' },
  customerIcon: { fontSize: '20px' },
  customerName: { fontSize: '14px', fontWeight: '600', color: '#fff' },
  customerEmail: { fontSize: '13px', color: '#999' },
  orderMessage: { display: 'flex', alignItems: 'flex-start', gap: '12px', marginTop: '12px', padding: '12px', background: 'rgba(255, 255, 255, 0.03)', borderRadius: '8px', borderLeft: '3px solid rgba(206, 0, 33, 0.5)' },
  messageIcon: { fontSize: '16px' },
  messageText: { fontSize: '14px', color: '#ccc', lineHeight: '1.5' },
  orderFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '16px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' },
  orderPrice: { fontSize: '18px', fontWeight: 'bold', color: '#ce0021' },
  orderActions: { display: 'flex', gap: '8px' },
  actionButton: { padding: '8px 16px', background: 'rgba(206, 0, 33, 0.1)', border: '1px solid rgba(206, 0, 33, 0.3)', borderRadius: '6px', color: '#ce0021', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  actionButtonDanger: { padding: '8px 16px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '6px', color: '#999', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }
};

// Add keyframes for spinner
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);
